import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Create a new treatment plan
export const createTreatmentPlan = createAsyncThunk(
  "treatmentPlan/create",
  async (treatmentPlanData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Creating treatment plan for client: ${treatmentPlanData.clientName}`);

      const response = await axios.post(
        `${baseURL}/api/treatment-plan`,
        treatmentPlanData,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully created treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error creating treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch all treatment plans
export const fetchTreatmentPlans = createAsyncThunk(
  "treatmentPlan/fetchAll",
  async (params = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching treatment plans with params:`, params);

      const queryParams = new URLSearchParams();
      if (params.clientId) queryParams.append('clientId', params.clientId);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan?${queryParams.toString()}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched ${response.data.docs?.length || 0} treatment plans`);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching treatment plans:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch a specific treatment plan by ID
export const fetchTreatmentPlanById = createAsyncThunk(
  "treatmentPlan/fetchById",
  async (treatmentPlanId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching treatment plan: ${treatmentPlanId}`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update a treatment plan
export const updateTreatmentPlan = createAsyncThunk(
  "treatmentPlan/update",
  async ({ treatmentPlanId, treatmentPlanData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Updating treatment plan: ${treatmentPlanId}`);

      const response = await axios.put(
        `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
        treatmentPlanData,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully updated treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error updating treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Delete a treatment plan
export const deleteTreatmentPlan = createAsyncThunk(
  "treatmentPlan/delete",
  async (treatmentPlanId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Deleting treatment plan: ${treatmentPlanId}`);

      const response = await axios.delete(
        `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully deleted treatment plan`);
      return { treatmentPlanId, message: response.data.message };
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error deleting treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch treatment plans for a specific client
export const fetchClientTreatmentPlans = createAsyncThunk(
  "treatmentPlan/fetchByClient",
  async ({ clientId, page = 1, limit = 10 }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching treatment plans for client: ${clientId}`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/client/${clientId}?page=${page}&limit=${limit}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched client treatment plans:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching client treatment plans:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch latest treatment plan for a client (for auto-loading goals in progress summary)
export const fetchLatestTreatmentPlan = createAsyncThunk(
  "treatmentPlan/fetchLatest",
  async (clientId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching latest treatment plan for client: ${clientId}`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/client/${clientId}/latest`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched latest treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching latest treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
